/**
 * useIntegrationListing
 *
 * Custom hook that coordinates data fetching and state updates for the
 * IntegrationListing feature.
 *
 * Responsibilities:
 *  - Watch `integrationQueryParamsAtom` for changes (filters, pagination,
 *    ordering, refresh triggers).
 *  - Invoke the service layer and write results into `integrationDataAtom`.
 *  - Guard against fetching before query-string filters are hydrated.
 *  - Expose a manual `refresh()` helper.
 */

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { showFailureToast } from 'lib/ToastHelper';
import { useEffect } from 'react';

import { fetchIntegrations } from '../services/integrationListingService';
import {
  integrationDataAtom,
  integrationDataRefreshAtom,
  integrationQueryParamsAtom,
  isQuerystringFiltersInitializedAtom,
} from '../state';

export function useIntegrationListing() {
  const queryParams = useAtomValue(integrationQueryParamsAtom);
  const isInitialized = useAtomValue(isQuerystringFiltersInitializedAtom);
  const [listingData, setListingData] = useAtom(integrationDataAtom);
  const setRefresh = useSetAtom(integrationDataRefreshAtom);

  useEffect(() => {
    // Wait until URL query-string filters have been read and applied
    if (!isInitialized) return;

    let cancelled = false;

    const loadData = async () => {
      setListingData({ ...listingData, loading: true });

      try {
        const response = await fetchIntegrations(queryParams);

        if (!cancelled) {
          setListingData({
            results: response.results ?? [],
            count: response.count ?? 0,
            loading: false,
          });
        }
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[useIntegrationListing] fetch error:', err);
          showFailureToast('Failed to load integrations. Please try again.');
          setListingData({
            results: [],
            count: 0,
            loading: false,
          });
        }
      }
    };

    void loadData();

    // Cancel state updates if the effect re-runs before the previous fetch
    // resolves (prevents stale writes on rapid filter changes).
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, isInitialized]);

  /** Force a fresh API call without changing any filter/pagination atoms */
  const refresh = () => setRefresh(new Date().toISOString());

  return {
    data: listingData.results,
    count: listingData.count,
    loading: listingData.loading,
    refresh,
  };
}
